// @safetyhub/report-composer — 바인딩 트리 순회 유틸
//
// ReportTemplate.body(ComposerNode[])를 재귀 순회하며 **모든 binding**을 소유자 정보와 함께
// 방문한다. writeForm(작성폼 파생)·admin(중복 키 경고)이 동일 순회 구조를 각자 복제하던 것을
// 단일 소스로 통합한 것이다(순회 구조 = resolver.walkNode / writeForm.collectBindings와 동형).
//
// 순회 대상(트리 순서):
//   - 노드 자신의 binding (source: 'node')
//   - group/stack children, repeat template로 재귀
//   - table 셀의 binding (source: 'cell')
//   - table 셀 compute.inputs[].binding (source: 'computeInput')
//       → resolver.walkNode가 이 입력 키를 RenderData에 적재하므로(값 공유 실재) 방문한다.
//         작성폼 위젯 파생 대상은 아니므로 deriveWriteForm은 이 source를 제외한다.
//   - table 셀 content(중첩 노드)로 재귀
//
// aggregate는 binding을 갖지 않으므로(over 경로 문자열) 방문 대상이 없다.

import type { Binding, ComposerNode } from '@safetyhub/shared';

/** 방문된 binding의 소유자 — 노드 또는 표 셀. source로 방문 경로를 구분한다. */
export interface BindingOwner {
  /** source==='node'일 때 소유 노드 id */
  nodeId?: string;
  /** source==='node'일 때 소유 노드 자체(kind별 위젯 파생 등에 사용). cell 경로에선 미제공. */
  node?: ComposerNode;
  /** source==='cell'|'computeInput'일 때 소유 셀 좌표 */
  cell?: { tableId: string; row: number; col: number };
  /**
   * 방문 경로: 노드 binding / 표 셀 binding / 표 셀 compute 입력 binding /
   * branch 조건 데이터 binding(conditionBinding).
   * conditionBinding은 nodeId=소유 branch id, node=branch 노드로 방문한다. computeBindingSignature
   * (미리보기 자동 재조회)가 자동 감지하나, deriveWriteForm은 작성폼 대상이 아니라 제외한다.
   */
  source: 'node' | 'cell' | 'computeInput' | 'conditionBinding';
  /** source==='conditionBinding'일 때 그 조건 식별 키(RenderData 적재 키) */
  conditionKey?: string;
}

/**
 * nodes 트리를 재귀 순회하며 각 binding을 visit(binding, owner)로 전달한다.
 *
 * 순회 순서/구조는 resolver.walkNode·writeForm.collectBindings와 동형이라, computeInput을
 * 걸러내면 두 소비처의 기존 산출이 그대로 재현된다(회귀 0). owner.source로 방문 경로를 구분.
 */
export function walkBindings(
  nodes: ComposerNode[],
  visit: (binding: Binding, owner: BindingOwner) => void,
): void {
  for (const node of nodes) {
    if (node.binding) visit(node.binding, { nodeId: node.id, node, source: 'node' });

    switch (node.kind) {
      case 'group':
      case 'stack':
        walkBindings(node.children, visit);
        break;
      case 'repeat':
        walkBindings([node.template], visit);
        break;
      case 'branch':
        for (const cb of node.conditionBindings ?? []) {
          visit(cb.binding, {
            nodeId: node.id,
            node,
            source: 'conditionBinding',
            conditionKey: cb.key,
          });
        }
        for (const slot of node.branches) walkBindings(slot.children, visit);
        break;
      case 'table':
        for (const cell of node.cells) {
          const cellOwner = { tableId: node.id, row: cell.row, col: cell.col };
          if (cell.binding) visit(cell.binding, { cell: cellOwner, source: 'cell' });
          if (cell.compute) {
            for (const inp of cell.compute.inputs) {
              if (inp.binding) visit(inp.binding, { cell: cellOwner, source: 'computeInput' });
            }
          }
          if (cell.content) walkBindings(cell.content, visit);
        }
        break;
      default:
        break;
    }
  }
}
